import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  History, Loader2, ArrowLeft, Search, RefreshCw, ShieldAlert,
  Monitor, Smartphone, Tablet, Bot, MapPin, ExternalLink, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEVICE_ICON = { mobile: Smartphone, tablet: Tablet, desktop: Monitor, bot: Bot };
const DEVICES = ['all', 'desktop', 'mobile', 'tablet', 'bot'];
const REFRESH_MS = 30000; // 30 seconds instead of 10

const humanDuration = (seconds) => {
  if (!seconds || seconds < 1) return '<1s';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const AdminVisitorHistoryPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [device, setDevice] = useState('all');
  const [page, setPage] = useState(1);
  const [limit] = useState(30);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = { page, limit };
      if (q.trim()) params.q = q.trim();
      if (device !== 'all') params.device = device;
      const { data } = await axios.get(`${API}/admin/visitor-history`, { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch {
      if (!silent) toast.error('Failed to load visitor history');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [q, device, page, limit]);

  useEffect(() => { load(); }, [load]);

  // Live polling every 30s (silent)
  useEffect(() => {
    const t = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [q, device]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const blockIp = async (ip) => {
    if (!window.confirm(`Block IP ${ip}? They'll be refused at checkout.`)) return;
    try {
      await axios.post(`${API}/admin/blocklist`, { type: 'ip', value: ip, reason: 'Blocked from visitor history' });
      toast.success('IP blocked');
      load(true);
    } catch {
      toast.error('Block failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 md:p-10">
        <button
          onClick={() => navigate('/admin/home')}
          className="text-xs text-[#71717A] hover:text-[#00F0FF] inline-flex items-center gap-1 mb-2"
          data-testid="back-to-admin-home"
        >
          <ArrowLeft size={12} /> Admin home
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-3">
            <History className="text-[#00F0FF]" size={26} /> Visitor History
          </h1>
          <button
            onClick={() => load()}
            className="inline-flex items-center gap-2 px-3 py-2 text-xs rounded-full border border-[#27272A] hover:border-[#00F0FF]"
            data-testid="visitor-history-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <p className="text-sm text-[#A1A1AA] mb-6">
          Last 48 hours of traffic — one row per session. Updates live every 10s. Rows older than 48h are purged automatically.
        </p>

        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by IP, city, country, path, referrer, or browser…"
              className="w-full pl-9 pr-3 py-2 text-sm bg-[#121214] border border-[#27272A] rounded-md focus:border-[#00F0FF] focus:outline-none"
              data-testid="visitor-history-search"
            />
          </div>
          {DEVICES.map((d) => (
            <button
              key={d}
              onClick={() => setDevice(d)}
              className={`px-3 py-1.5 text-xs rounded-full border capitalize ${
                device === d ? 'border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46]'
              }`}
              data-testid={`visitor-history-device-${d}`}
            >
              {d}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#71717A]"><Loader2 className="mx-auto animate-spin mb-3" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 bg-[#121214] border border-[#27272A] rounded-lg text-sm text-[#71717A]" data-testid="visitor-history-empty">
            No visitors matching the current filter yet.
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block bg-[#121214] border border-[#27272A] rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[1080px]">
                <thead className="bg-[#0F0F11] text-[10px] uppercase tracking-wider text-[#71717A]">
                  <tr>
                    <th className="text-left px-3 py-2">Last active</th>
                    <th className="text-left px-3 py-2">IP</th>
                    <th className="text-left px-3 py-2">Location</th>
                    <th className="text-left px-3 py-2">Device</th>
                    <th className="text-left px-3 py-2">Referrer</th>
                    <th className="text-left px-3 py-2">Landing → Current</th>
                    <th className="text-left px-3 py-2">Duration</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const Icon = DEVICE_ICON[r.device_type] || Monitor;
                    return (
                      <tr key={r.session_id} className="border-t border-[#27272A] hover:bg-[#181819]" data-testid={`vh-row-${r.session_id}`}>
                        <td className="px-3 py-2 text-xs text-[#D4D4D8] whitespace-nowrap">
                          <div>{new Date(r.timestamp).toLocaleString()}</div>
                          <div className="text-[10px] text-[#71717A]"><Clock size={10} className="inline mr-1" />{r.hits || 1} hits</div>
                        </td>
                        <td className="px-3 py-2 text-xs font-mono">
                          {r.blocked ? (
                            <span className="inline-flex items-center gap-1 text-red-400"><ShieldAlert size={10} /> {r.ip_address}</span>
                          ) : (
                            <span>{r.ip_address}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-[#A1A1AA]">
                          {(r.city || r.country) ? (
                            <span className="inline-flex items-start gap-1">
                              <MapPin size={10} className="mt-0.5 text-[#71717A]" />
                              <span>{[r.city, r.region, r.country].filter(Boolean).join(', ')}</span>
                            </span>
                          ) : (
                            <span className="text-[#52525B]">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <div className="inline-flex items-center gap-1">
                            <Icon size={12} className="text-[#71717A]" />
                            <span className="text-[#D4D4D8] capitalize">{r.device_type}</span>
                          </div>
                          <div className="text-[10px] text-[#71717A]">{r.browser} · {r.os}</div>
                        </td>
                        <td className="px-3 py-2 text-xs text-[#A1A1AA]">
                          <span className="capitalize">{r.platform || 'direct'}</span>
                        </td>
                        <td className="px-3 py-2 text-xs text-[#D4D4D8] max-w-[240px] truncate">
                          <span title={r.path}>{r.path}</span>
                        </td>
                        <td className="px-3 py-2 text-xs text-[#71717A] whitespace-nowrap">{humanDuration(r.duration_seconds)}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {!r.blocked && (
                            <button
                              onClick={() => blockIp(r.ip_address)}
                              className="text-[10px] px-2 py-1 rounded-full border border-[#27272A] hover:border-red-500 hover:text-red-400"
                              data-testid={`vh-block-${r.session_id}`}
                            >
                              Block IP
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-3">
              {rows.map((r) => {
                const Icon = DEVICE_ICON[r.device_type] || Monitor;
                return (
                  <div key={r.session_id} className="bg-[#121214] border border-[#27272A] rounded-lg p-4" data-testid={`vh-card-${r.session_id}`}>
                    <div className="flex items-center gap-2 justify-between mb-1">
                      <div className="text-xs text-[#A1A1AA]">{new Date(r.timestamp).toLocaleString()}</div>
                      <div className="inline-flex items-center gap-1 text-xs">
                        <Icon size={12} className="text-[#71717A]" />
                        <span className="capitalize">{r.device_type}</span>
                      </div>
                    </div>
                    <div className={`text-sm font-mono mb-2 ${r.blocked ? 'text-red-400' : ''}`}>
                      {r.blocked && <ShieldAlert size={12} className="inline mr-1" />}
                      {r.ip_address}
                    </div>
                    <div className="text-xs text-[#A1A1AA] mb-1">
                      {[r.city, r.country].filter(Boolean).join(', ') || '—'}
                    </div>
                    <div className="text-xs text-[#71717A] mb-1">{r.browser} · {r.os}</div>
                    <div className="text-xs text-[#D4D4D8] break-all mb-2">{r.path}</div>
                    <div className="flex items-center justify-between text-[10px] text-[#71717A]">
                      <span>{humanDuration(r.duration_seconds)} · {r.hits || 1} hits</span>
                      {!r.blocked && (
                        <button
                          onClick={() => blockIp(r.ip_address)}
                          className="text-[10px] px-2 py-1 rounded-full border border-[#27272A] hover:border-red-500 hover:text-red-400"
                        >
                          Block IP
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-center gap-2 text-xs">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="px-3 py-1.5 rounded-full border border-[#27272A] disabled:opacity-40"
                  data-testid="visitor-history-prev"
                >Prev</button>
                <span className="text-[#A1A1AA]">Page {page} / {totalPages}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  className="px-3 py-1.5 rounded-full border border-[#27272A] disabled:opacity-40"
                  data-testid="visitor-history-next"
                >Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminVisitorHistoryPage;
